import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import { graphql, useStaticQuery } from "gatsby";
import React, { createContext } from "react";
import { PageNodeQuery } from "../config/build/models/PageNodeQuery";
import fallback from "../localization/localizationFiles/fallback.json";
import { Translations } from "../localization/Translations";
import { translationsPreviewQueryProjection } from "../preview/staticQueries/translations";
import { isNullOrWhiteSpace } from "../shared/string";

/**
 * Name of a language, in said language. Mainly used as
 * accessible labels for language selectors.
 */
export const localLanguageNames: Translations = {
  "en": "English",
  "en-us": "English",
  "fr-be": "Français",
  "fr-ch": "Français",
  "nl-be": "Nederlands",
  "de-ch": "Deutsch",
  "it-ch": "Italiano",
  "sv-se": "Svenska"
};

/**
 * Short display labels for language selectors. Falls back to
 * localLanguageNames for markets without a custom abbreviation.
 */
export const localLanguageDisplayLabels: Translations = {
  ...localLanguageNames,
  "fr-be": "FR",
  "nl-be": "NL"
};

export type LocalizationContextProviderProps = {
  children?: React.ReactNode;
  currentLanguage?: string;
};

const LocalizationContext = createContext<Translations>({});

const LocalizationContextProvider = ({
  currentLanguage,
  children
}: LocalizationContextProviderProps) => {
  const result = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<PageNodeQuery<Translations>>(graphql`
      query getTranslations {
        queryResult: allSanityTranslations {
          nodes {
            address
            all
            allAuthors
            allCalendarEvents
            allCategories
            allPests
            allPressReleases
            allReports
            allYears
            alreadyParticipantOfTheCourse
            anniversaryImageAlt
            article
            articlesBy
            attackingFoodTextiles
            attackingWood
            australia
            behavior
            biting
            blog
            blogArticle
            blogArticleCategoryFilterAlt
            blogPageNavigationTitle
            bookCourseCta
            bookCourseNowCta
            brandImage
            canada
            chooseCountry
            clearBlogFilters
            clearCourseFilters
            clickSelectFile
            close
            collapse
            countryOfResidence
            courseAvailableAtOnce
            courseDetail
            courseDetailDateAndTime
            courseDetailLocation
            courseDetailNumberOfSeats
            courseDetailPrice
            courseFormat
            courseFormatAllOption
            courseFormatBlended
            courseFormatClassroom
            courseFormatOnline
            courseInfoTitle
            courseLength
            courseLocationFilterLabel
            courseOnlineFilterLabel
            coursesLandingLinkToCourseCateogryListText
            coursesListHeading
            coursesReadMoreBookLinkText
            courseTraining
            crawling
            currentLanguageSelectLabel
            currentLocation
            date
            displayIn
            downloadImage
            dragAndDrop
            email
            error
            expand
            financialCalendarFilterAlt
            financialEvent
            flying
            getInTouch
            gnawing
            googleCustomerReviewsImageAlt
            goToMainContent
            goToOfficeLink
            gridTitle
            hiRes
            hits
            home
            hongkong
            i18nLang
            imageBankFilterAlt
            informationType
            invalidDate
            invalidNumber
            invalidPostalCode
            japan
            lastXYears
            learnTheTipsButton
            listTitle
            loadMoreButton
            locator
            lowRes
            management
            mapSearchPlaceholder
            maxFileSize
            maxLength
            maxValue
            media
            message
            minLength
            minValue
            moreCoursesHeading
            moreThan8Legs
            name
            news
            newZealand
            next
            no
            noLegs
            noWings
            numberOfLegs
            numberOfWings
            office
            officeHours
            offices
            officesInSameRegionSubtitle
            officesInSameRegionTitle
            or
            orCall
            orderByDate
            orderByRelevance
            other
            past
            person
            pest
            pest_2Legs
            pest_2Wings
            pest_4Legs
            pest_4Wings
            pest_6Legs
            pest_8Legs
            pestIdentifier
            phoneNumber
            policyCheckboxLabel
            postAuthorLabel
            poweredByGoogleImageAlt
            presentation
            pressImage
            pressRelease
            pressReleases
            pressReleaseTypeFilterAlt
            priceFree
            proceed
            published
            ratingDescription0
            ratingDescription1
            ratingDescription2
            ratingDescription3
            ratingDescription4
            readCaseButton
            readMore
            recaptcha
            regulatoryPressRelease
            regulatoryPressReleases
            relatedServicesAndLocatorsSubtitle
            relatedServicesAndLocatorsTitle
            remove
            report
            reports
            required
            reviewsAllReviewsLinkText
            reviewsReviewsCount
            reviewsSeeFullReviewLinkText
            searchAll
            searchFilterAlt
            searchLabel
            searchPlaceholder
            searchSortByAlt
            searchSubmit
            searchTitle
            seeAll
            selectLanguage
            sendEmail
            sendMessage
            service
            showOnMapButton
            southAfrica
            submit
            submitInvalid
            submitted
            submitting
            subscribeSubmit
            sweden
            title
            tooManyFiles
            troublingPeople
            typeOfIssue
            upcoming
            usa
            visitorAddress
            yearFilterAlt
            yes
          }
        }
      }
    `),
    query: translationsPreviewQueryProjection
  });

  let translations = result.queryResult.nodes.find(
    translation => translation.i18nLang === currentLanguage
  );

  if (translations) {
    // Fallback for each missing translation.
    // Use "fallback" as reference since it *should* contain all field names
    for (const field in fallback) {
      if (isNullOrWhiteSpace(translations[field])) {
        translations[field] = fallback[field];
      }
    }
  } else {
    console.warn(
      `No translations for language '${currentLanguage}' found, using fallback instead`
    );
    translations = fallback;
  }

  return (
    <LocalizationContext.Provider value={translations}>
      {children}
    </LocalizationContext.Provider>
  );
};

export { LocalizationContext, LocalizationContextProvider };
